import { Trans, useLingui } from '@lingui/react'
import { type CartV2 } from '@redteclab/api/clients/bully'
import classNames from 'clsx'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { NOTIFICATION_TYPES, SA_SELLER_ID } from 'shared/consts'
import {
  type IState,
  type NowDataState,
  type NowDataStateSlot,
} from 'types/redux'
import Image from 'views/components/atoms/Image'
import Price from 'views/components/atoms/Price'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import Notification from 'views/components/molecules/Notification'
import nowLogoRebrandingCOM from 'views/components/organisms/Cart/NowCollapsible/now_logo_rebranding_com.png'
import { NOW_SHIPPING_CART_MODAL_ID } from 'views/components/organisms/NowShippingModal/names'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import {
  MINIMUM_VALUE_NOT_REACHED,
  NO_AVAILABLE_SLOT,
  NOT_IN_DELIVERY_REGION,
  PRODUCTS_NOT_AVAILABLE,
  RX_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import { OPEN_NOW_CART_SHIPPING_MODAL } from 'views/containers/browser/NowShippingModalCartContainer/clientsideHook'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { capitalizeFirstLetter } from 'views/utils/capitalizeFirstLetter'
import {
  getSameDaySlotsSortedBySecondsLeft,
  setTimeleft,
} from 'views/utils/nowCountdownTimer'

interface PropsInterface {
  cart: CartV2
  setNowFaqModalOpen: Dispatch<SetStateAction<boolean>>
  nowData: NowDataState
  publicRuntimeConfig: {
    publicConfig: {
      locale: string
      currencySymbolRight: boolean
    }
  }
}

const WithZipCodeCase = (props: PropsInterface) => {
  const {
    cart,
    publicRuntimeConfig: {
      publicConfig: { locale, currencySymbolRight },
    },
  } = props

  const { i18n } = useLingui()

  const nowData = useSelector((state: IState) => state.nowData)

  const [errorCode, setErrorCode] = useState<number | null>(null)

  const secondsLeft =
    getSameDaySlotsSortedBySecondsLeft(nowData?.slots)[0]?.secondsLeft ?? 0

  const [countdownTime, setCountdownTime] = useState(secondsLeft.toString())

  const [timeLeft, setTimeLeft] = useState('')

  let slot: NowDataStateSlot | null = null

  if (nowData?.slots?.length) {
    slot = nowData.slots[0]
  }

  useEffect(() => {
    setCountdownTime(secondsLeft?.toString())
  }, [secondsLeft])

  useEffect(() => {
    const interval = setTimeleft(countdownTime, setTimeLeft, setCountdownTime)

    setErrorCode(nowData?.notEligibleReason ?? null)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [countdownTime, nowData])

  const minOrderAmount = nowData?.merchant?.minimumOrderValue
    ? nowData.merchant.minimumOrderValue / 100
    : 0

  const shippingCost = slot?.shippingCostsAmountDefault
    ? slot?.shippingCostsAmountDefault / 100
    : 0

  const differenceMinimumOrderValue = nowData?.centsDifferenceMinimumOrderValue
    ? nowData?.centsDifferenceMinimumOrderValue / 100
    : 0

  const saeSellerGroup = cart?.sellerGroups.find(
    it => it.sellerId === SA_SELLER_ID
  )

  const usingDiscounts = !!saeSellerGroup?.discounts?.length

  const usingAnyDiscounts =
    usingDiscounts || cart?.rxBonusDiscount > 0 || !!cart?.rxBonusTotal

  const currencySymbol = currencyToSymbol('EUR')
  const diffPrice = formatPriceString({
    amount: differenceMinimumOrderValue,
    locale,
    currencySymbol,
    symbolRight: currencySymbolRight,
  })

  const isCartEligible: boolean = !errorCode && !!slot

  const experimentOrOriginalText = (
    <TransWithHtml
      text={i18n._(
        'general.label.now.collapsible.error.minimum_value_not_reached',
        { diffPrice }
      )}
      className="o-NowCollapsible__error40"
    />
  )

  return (
    <div
      className={classNames('with-zip-code-case', 'l-flex--wrap', {
        'o-NowCollapsible__positive': !errorCode,
        'o-NowCollapsible__defect': errorCode,
        'o-NowCollapsible__cart-not-in-delivery-region':
          errorCode === NOT_IN_DELIVERY_REGION,
      })}
    >
      <div className="l-flex l-flex--start">
        <div className="l-flex">
          <Image
            className="u-margin--small u-padding-small--right o-NowCollapsible__now-logo"
            src={nowLogoRebrandingCOM}
            alt="now-logo"
          />
        </div>
        <div className={'u-padding-small--left'}>
          <div
            className={classNames({
              'o-NowCollapsible__cart-eligible': !isCartEligible,
              'o-NowCollapsible__cart-not-eligible': errorCode,
            })}
          >
            <div hidden={!isCartEligible}>
              <h2 className={'o-NowCollapsible__title u-color--positive'}>
                {capitalizeFirstLetter(slot ? slot?.deliveryDay : '')},{' '}
                {slot?.deliveryTimeInfo}
              </h2>
              <p
                className={
                  'o-NowCollapsible__below-time-data u-color--positive'
                }
              >
                <Trans
                  id={'general.label.now.collapsible.ok'}
                  values={{ timeLeft }}
                />
              </p>
              <p>
                {shippingCost === 0 && (
                  <>
                    <Trans id={'general.label.now.freeShipping'} /> <br />
                  </>
                )}
                <Trans id={'general.label.now.minimumOrder'} />{' '}
                <Price flat amount={minOrderAmount} />
                <br />
                <Trans
                  id={'general.label.now.minimumShippingCost'}
                  components={{ 0: <span className={'u-color--grey'} /> }}
                  values={{
                    amount: <Price amount={shippingCost} />,
                    minimumAmount: (
                      <Price
                        amount={
                          nowData?.noShippingCostsIfTotalMinimumCents &&
                          nowData?.noShippingCostsIfTotalMinimumCents / 100
                        }
                      />
                    ),
                  }}
                />
                <br />
                <Trans id={'general.label.now.zipCodeArea'} />:{' '}
                <ButtonAsLink
                  className={'o-NowCollapsible__zip-code-area-value'}
                  id={
                    'check-now-eligibility-not-in-delivery-region-button-zip-code'
                  }
                  underlined
                  data-clientside-hook={OPEN_NOW_CART_SHIPPING_MODAL}
                  data-a11y-dialog-show={NOW_SHIPPING_CART_MODAL_ID}
                >
                  {nowData?.zipCode}
                </ButtonAsLink>
              </p>
            </div>
            <div hidden={errorCode !== NOT_IN_DELIVERY_REGION}>
              <p>
                <Trans
                  id={
                    'general.label.now.collapsible.error.not_in_delivery_region'
                  }
                />
              </p>

              <ButtonAsLink
                id={'check-now-eligibility-not-in-delivery-region-button'}
                bold
                data-clientside-hook={OPEN_NOW_CART_SHIPPING_MODAL}
                data-a11y-dialog-show={NOW_SHIPPING_CART_MODAL_ID}
              >
                <Trans id={'general.label.now.collapsible.error10_button'} />
              </ButtonAsLink>
            </div>

            {errorCode === NO_AVAILABLE_SLOT && (
              <Trans
                id={'general.label.now.collapsible.error.no_available_slot'}
              />
            )}

            {errorCode === MINIMUM_VALUE_NOT_REACHED &&
              experimentOrOriginalText}

            {(errorCode === PRODUCTS_NOT_AVAILABLE ||
              errorCode === RX_NOT_AVAILABLE) && (
              <Trans
                id={'general.label.now.collapsible.error.product_not_available'}
              />
            )}
          </div>
        </div>
      </div>
      {!errorCode && slot && usingAnyDiscounts && (
        <div
          className={'o-NowCollapsible__voucher-info-wrapper'}
          data-qa-id="now-collapsible-info-notCombinables"
        >
          <Notification
            label={i18n._('general.label.now.collapsible.vaucherInformation')}
            type={NOTIFICATION_TYPES.INFO}
          />
        </div>
      )}
    </div>
  )
}

export default withShopAndRuntimeConfig(WithZipCodeCase)
